import { supabase } from "@/integrations/supabase/client";

export async function recordBraceletScan(braceletId: string, churchCode: string, userId?: string) {
  try {
    console.log(`Recording scan for bracelet ${braceletId}, church ${churchCode}, user ${userId || 'anonymous'}`);
    
    const cleanBraceletId = braceletId.replace(/^GHG-?/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // For TEST bracelets, ALWAYS use church code 001
    // For non-TEST bracelets without a specific church, use 'GRACE' as default
    const finalChurchCode = cleanBraceletId.startsWith('TEST') ? '001' : (churchCode || 'GRACE');
    
    console.log(`Using church code ${finalChurchCode} for bracelet ${cleanBraceletId}`);
    
    // Always check if this bracelet exists in the bracelets table
    const { data: existingBracelet, error: braceletCheckError } = await supabase
      .from("bracelets")
      .select("*")
      .eq("referral_code", cleanBraceletId)
      .maybeSingle();
      
    if (braceletCheckError) {
      console.error("Error checking if bracelet exists:", braceletCheckError);
      
      // Continue for all bracelets regardless of errors checking
      console.log("Continuing despite error checking bracelet existence");
    }
    
    // If bracelet doesn't exist, create it
    if (!existingBracelet) {
      console.log(`Bracelet ${cleanBraceletId} doesn't exist in database, creating it now`);
      
      try {
        const { error: createError } = await supabase
          .from("bracelets")
          .insert({
            referral_code: cleanBraceletId,
            church_code: finalChurchCode,
            created_at: new Date().toISOString()
          });
          
        if (createError) {
          console.error(`Error creating bracelet ${cleanBraceletId}:`, createError);
          // Continue despite errors for all bracelets
          console.log("Continuing despite error creating bracelet");
        } else {
          console.log(`Created bracelet ${cleanBraceletId} in database with church code ${finalChurchCode}`);
        }
      } catch (err) {
        console.error(`Failed to create bracelet ${cleanBraceletId}:`, err);
        // Continue despite errors for all bracelets
        console.log("Continuing despite exception when creating bracelet");
      }
    } else if (existingBracelet.church_code !== finalChurchCode && cleanBraceletId.startsWith('TEST')) {
      // Update church code for TEST bracelets if needed
      console.log(`Updating church code for TEST bracelet ${cleanBraceletId} from ${existingBracelet.church_code} to ${finalChurchCode}`);
      const { error: updateError } = await supabase
        .from("bracelets")
        .update({ church_code: finalChurchCode })
        .eq("referral_code", cleanBraceletId);
        
      if (updateError) {
        console.error(`Error updating church code for bracelet ${cleanBraceletId}:`, updateError);
        // Continue despite errors for TEST bracelets
      }
    }
    
    // Always record the scan regardless of bracelet creation success
    console.log(`Recording scan for bracelet ${cleanBraceletId} with church code ${finalChurchCode}`);
    
    // Record scan for all bracelets, not just TEST or authenticated users
    const { data: scanData, error: scanError } = await supabase
      .from("bracelet_scans")
      .insert({
        bracelet_id: cleanBraceletId,
        church_code: finalChurchCode,
        scanned_by_user_id: userId || null,
        scan_location: "App Scan",
        scan_device: navigator.userAgent || "Unknown"
      })
      .select();
      
    if (scanError) {
      console.error("Error recording bracelet scan:", scanError);
      return false; // Return false only if scan recording fails
    }
    
    console.log("Bracelet scan recorded successfully:", scanData);
    return true;
  } catch (error) {
    console.error("Error recording bracelet scan:", error);
    return false;
  }
}

export async function getTotalScansForChurch(churchCode: string): Promise<number> {
  try {
    console.log(`Getting total scans for church code: ${churchCode}`);
    
    const { count, error } = await supabase
      .from("bracelet_scans")
      .select("*", { count: "exact", head: true })
      .eq("church_code", churchCode);
      
    if (error) {
      console.error("Error getting scans count:", error);
      throw error;
    }
    
    console.log(`Found ${count} total scans for church ${churchCode}`);
    return count || 0;
  } catch (error) {
    console.error("Error getting scans count:", error);
    return 0;
  }
}
